import React, { useEffect, useState, useCallback } from 'react'
import {
  collection, doc, getDocs, addDoc, deleteDoc, updateDoc,
  onSnapshot, orderBy, query, serverTimestamp
} from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import AdminLayout from './AdminLayout'
import { detectType, extractVideoId } from '../../utils/archiveUtils'

const TYPES = [
  { value: 'sermon', label: 'Проповідь' },
  { value: 'word',   label: 'Вивчення Слова' },
  { value: 'events', label: 'Подія' },
  { value: 'choir',  label: 'Хор' },
  { value: 'online', label: 'Онлайн Трансляція' },
]

const typeLabel = (v) => (TYPES.find(t => t.value === v) || {}).label || v

const emptyPendingForm = { title: '', author: '', year: '', bible: '', keywords: '', type: 'sermon' }

// ─── Component ────────────────────────────────────────────────────────────────

const AdminArchive = () => {
  // pending
  const [pending, setPending] = useState([])
  const [expandedPending, setExpandedPending] = useState(null)
  const [pendingForms, setPendingForms] = useState({})
  const [approving, setApproving] = useState(null)
  const [skipping, setSkipping] = useState(null)

  // archive
  const [archive, setArchive] = useState([])
  const [archiveLoading, setArchiveLoading] = useState(true)
  const [archiveSearch, setArchiveSearch] = useState('')
  const [archivePage, setArchivePage] = useState(1)
  const [expandedArchive, setExpandedArchive] = useState(null)
  const [editForms, setEditForms] = useState({})
  const [saving, setSaving] = useState(null)
  const [deleting, setDeleting] = useState(null)

  const ARCHIVE_PAGE_SIZE = 30

  // ── pending live ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'pendingVideos'), orderBy('publishedAt', 'desc')),
      function(snap) {
        setPending(
          snap.docs
            .map(function(d) { return Object.assign({ id: d.id }, d.data()) })
            .filter(function(v) { return !v.skipped })
        )
      }
    )
    return unsub
  }, [])

  // ── archive load ─────────────────────────────────────────────────────────────

  var loadArchive = useCallback(function() {
    setArchiveLoading(true)
    getDocs(query(collection(db, 'archive'), orderBy('year', 'desc')))
      .then(function(snap) {
        setArchive(snap.docs.map(function(d) { return Object.assign({ id: d.id }, d.data()) }))
        setArchiveLoading(false)
      })
      .catch(function() { setArchiveLoading(false) })
  }, [])

  useEffect(function() { loadArchive() }, [loadArchive])

  // ── pending handlers ──────────────────────────────────────────────────────────

  var handlePendingExpand = function(id, video) {
    if (expandedPending === id) { setExpandedPending(null); return }
    setExpandedPending(id)
    if (!pendingForms[id]) {
      var year = video.publishedAt
        ? new Date(video.publishedAt).getFullYear()
        : new Date().getFullYear()
      setPendingForms(function(f) {
        var next = Object.assign({}, f)
        next[id] = Object.assign({}, emptyPendingForm, { title: video.title || '', year: year, type: detectType(video.title) })
        return next
      })
    }
  }

  var setPendingField = function(id, field, val) {
    setPendingForms(function(f) {
      var next = Object.assign({}, f)
      next[id] = Object.assign({}, f[id], { [field]: val })
      return next
    })
  }

  var handleApprove = async function(video) {
    var form = pendingForms[video.id]
    var isOnline = form && form.type === 'online'
    if (!form || !form.year) { alert('Вкажіть рік'); return }
    if (!isOnline && !form.author.trim()) { alert('Вкажіть автора'); return }
    setApproving(video.id)
    try {
      await addDoc(collection(db, 'archive'), {
        title:     form.title.trim(),
        author:    isOnline ? 'Онлайн Трансляція' : form.author.trim(),
        year:      Number(form.year),
        bible:     form.bible.trim(),
        keywords:  form.keywords.split(',').map(function(k) { return k.trim() }).filter(Boolean),
        type:      form.type,
        link:      video.link,
        videoId:   video.id,
        thumbnail: video.thumbnail || '',
        createdAt: serverTimestamp(),
      })
      await deleteDoc(doc(db, 'pendingVideos', video.id))
      loadArchive()
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setApproving(null)
    }
  }

  var handleSkip = async function(id) {
    if (!window.confirm('Пропустити це відео? Воно більше не з\'являтиметься у черзі.')) return
    setSkipping(id)
    try {
      await updateDoc(doc(db, 'pendingVideos', id), { skipped: true })
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setSkipping(null)
    }
  }

  // ── archive handlers ──────────────────────────────────────────────────────────

  var handleArchiveExpand = function(item) {
    if (expandedArchive === item.id) { setExpandedArchive(null); return }
    setExpandedArchive(item.id)
    if (!editForms[item.id]) {
      setEditForms(function(f) {
        var next = Object.assign({}, f)
        next[item.id] = {
          title:    item.title || '',
          author:   item.author || '',
          year:     item.year || '',
          bible:    item.bible || '',
          keywords: Array.isArray(item.keywords) ? item.keywords.join(', ') : '',
          type:     item.type || 'sermon',
        }
        return next
      })
    }
  }

  var setEditField = function(id, field, val) {
    setEditForms(function(f) {
      var next = Object.assign({}, f)
      next[id] = Object.assign({}, f[id], { [field]: val })
      return next
    })
  }

  var handleArchiveSave = async function(id) {
    var form = editForms[id]
    if (!form.title.trim() || !form.year) { alert('Назва та рік обов\'язкові'); return }
    setSaving(id)
    try {
      await updateDoc(doc(db, 'archive', id), {
        title:    form.title.trim(),
        author:   form.type === 'online' ? 'Онлайн Трансляція' : form.author.trim(),
        year:     Number(form.year),
        bible:    form.bible.trim(),
        keywords: form.keywords.split(',').map(function(k) { return k.trim() }).filter(Boolean),
        type:     form.type,
      })
      setExpandedArchive(null)
      loadArchive()
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setSaving(null)
    }
  }

  var handleArchiveDelete = async function(id, title) {
    if (!window.confirm('Видалити "' + title + '" з архіву?')) return
    setDeleting(id)
    try {
      await deleteDoc(doc(db, 'archive', id))
      loadArchive()
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setDeleting(null)
    }
  }

  // ── archive filtering + pagination ────────────────────────────────────────────

  var term = archiveSearch.trim().toLowerCase()
  var filtered = term
    ? archive.filter(function(v) {
        return (v.title || '').toLowerCase().includes(term)
            || (v.author || '').toLowerCase().includes(term)
            || String(v.year).includes(term)
            || typeLabel(v.type).toLowerCase().includes(term)
      })
    : archive

  var totalPages = Math.max(1, Math.ceil(filtered.length / ARCHIVE_PAGE_SIZE))
  var safePage = Math.min(archivePage, totalPages)
  var pageItems = filtered.slice((safePage - 1) * ARCHIVE_PAGE_SIZE, safePage * ARCHIVE_PAGE_SIZE)

  // reset page on new search
  useEffect(function() { setArchivePage(1) }, [archiveSearch])

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <div style={s.page}>
        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>Архів відео</h1>
            <p style={s.pageDesc}>
              {archive.length} записів в архіві · {pending.length} в черзі на розгляд
            </p>
          </div>
        </div>

        {/* ── Pending ── */}
        <div style={{ ...s.card, marginBottom: '24px' }}>
          <div style={s.cardHeader}>
            <span style={s.cardIcon}>🎬</span>
            <span style={s.cardTitle}>Нові відео з YouTube</span>
            <span style={s.badge}>{pending.length}</span>
          </div>

          {pending.length === 0 && (
            <div style={s.empty}>Черга порожня. Запустіть синхронізацію або зачекайте cron-у.</div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pending.map(function(video) {
              var form = pendingForms[video.id] || emptyPendingForm
              var isOpen = expandedPending === video.id
              var isOnline = form.type === 'online'
              return (
                <div key={video.id} style={s.pendingRow}>
                  <div style={s.pendingSummary} onClick={function() { handlePendingExpand(video.id, video) }}>
                    {video.thumbnail && <img src={video.thumbnail} alt='' style={s.thumb} />}
                    <div style={s.pendingInfo}>
                      <div style={s.pendingTitle}>{video.title}</div>
                      <div style={s.pendingMeta}>{video.publishedAt ? video.publishedAt.slice(0, 10) : ''}</div>
                    </div>
                    <div style={s.pendingActions}>
                      <a href={video.link} target='_blank' rel='noopener noreferrer'
                        onClick={function(e) { e.stopPropagation() }} style={s.ytLink}>
                        ▶ YouTube
                      </a>
                      <span style={{ ...s.expandBtn, transform: isOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={s.form}>
                      <div style={s.formGrid}>
                        <div style={s.field}>
                          <label style={s.label}>Назва</label>
                          <input value={form.title}
                            onChange={function(e) { setPendingField(video.id, 'title', e.target.value) }}
                            style={s.input} />
                        </div>
                        <div style={s.field}>
                          <label style={s.label}>Тип</label>
                          <select value={form.type}
                            onChange={function(e) { setPendingField(video.id, 'type', e.target.value) }}
                            style={s.input}>
                            {TYPES.map(function(t) { return <option key={t.value} value={t.value}>{t.label}</option> })}
                          </select>
                        </div>
                        {!isOnline && (
                          <div style={s.field}>
                            <label style={s.label}>Автор *</label>
                            <input value={form.author}
                              onChange={function(e) { setPendingField(video.id, 'author', e.target.value) }}
                              style={s.input} placeholder="Прізвище Ім'я" />
                          </div>
                        )}
                        <div style={s.field}>
                          <label style={s.label}>Рік *</label>
                          <input type='number' value={form.year}
                            onChange={function(e) { setPendingField(video.id, 'year', e.target.value) }}
                            style={s.input} min='2000' max='2099' />
                        </div>
                        {!isOnline && (
                          <div style={s.field}>
                            <label style={s.label}>Біблійний текст</label>
                            <input value={form.bible}
                              onChange={function(e) { setPendingField(video.id, 'bible', e.target.value) }}
                              style={s.input} placeholder='напр. Іс. 40:31' />
                          </div>
                        )}
                        <div style={s.field}>
                          <label style={s.label}>Ключові слова (через кому)</label>
                          <input value={form.keywords}
                            onChange={function(e) { setPendingField(video.id, 'keywords', e.target.value) }}
                            style={s.input} placeholder='віра, надія, молитва' />
                        </div>
                      </div>
                      <div style={s.btnRow}>
                        <button onClick={function() { handleApprove(video) }}
                          disabled={approving === video.id} style={s.btnPrimary}>
                          {approving === video.id ? 'Зберігаємо…' : '✓ Додати в архів'}
                        </button>
                        <button onClick={function() { handleSkip(video.id) }}
                          disabled={skipping === video.id} style={s.btnGhost}>
                          {skipping === video.id ? '…' : '✗ Пропустити'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Archive management ── */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardIcon}>📁</span>
            <span style={s.cardTitle}>Записи в архіві</span>
            <span style={s.badge}>{filtered.length}</span>
            <input
              value={archiveSearch}
              onChange={function(e) { setArchiveSearch(e.target.value) }}
              placeholder='Пошук…'
              style={{ ...s.input, maxWidth: '220px', marginLeft: 'auto' }}
            />
            <button onClick={loadArchive} style={s.btnGhost} title='Оновити'>↺</button>
          </div>

          {archiveLoading && <div style={s.empty}>Завантаження…</div>}

          {!archiveLoading && filtered.length === 0 && (
            <div style={s.empty}>Нічого не знайдено</div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {pageItems.map(function(item) {
              var isOpen = expandedArchive === item.id
              var form = editForms[item.id] || {}
              var isOnline = (isOpen ? form.type : item.type) === 'online'
              return (
                <div key={item.id} style={s.archiveRow}>
                  <div style={s.archiveSummary}>
                    <span style={s.archiveYear}>{item.year}</span>
                    <span style={s.archiveTitle} onClick={function() { handleArchiveExpand(item) }}>
                      {item.title}
                    </span>
                    <span style={s.archiveAuthor}>{item.author}</span>
                    <span style={s.archiveType}>{typeLabel(item.type)}</span>
                    <div style={s.archiveActions}>
                      <button onClick={function() { handleArchiveExpand(item) }}
                        style={{ ...s.iconBtn, color: '#6b4a32' }}>✏️</button>
                      <button
                        onClick={function() { handleArchiveDelete(item.id, item.title) }}
                        disabled={deleting === item.id}
                        style={{ ...s.iconBtn, color: '#b84040' }}>
                        {deleting === item.id ? '…' : '🗑'}
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={s.form}>
                      <div style={s.formGrid}>
                        <div style={s.field}>
                          <label style={s.label}>Назва</label>
                          <input value={form.title || ''}
                            onChange={function(e) { setEditField(item.id, 'title', e.target.value) }}
                            style={s.input} />
                        </div>
                        <div style={s.field}>
                          <label style={s.label}>Тип</label>
                          <select value={form.type || 'sermon'}
                            onChange={function(e) { setEditField(item.id, 'type', e.target.value) }}
                            style={s.input}>
                            {TYPES.map(function(t) { return <option key={t.value} value={t.value}>{t.label}</option> })}
                          </select>
                        </div>
                        {!isOnline && (
                          <div style={s.field}>
                            <label style={s.label}>Автор</label>
                            <input value={form.author || ''}
                              onChange={function(e) { setEditField(item.id, 'author', e.target.value) }}
                              style={s.input} />
                          </div>
                        )}
                        <div style={s.field}>
                          <label style={s.label}>Рік</label>
                          <input type='number' value={form.year || ''}
                            onChange={function(e) { setEditField(item.id, 'year', e.target.value) }}
                            style={s.input} />
                        </div>
                        {!isOnline && (
                          <div style={s.field}>
                            <label style={s.label}>Біблійний текст</label>
                            <input value={form.bible || ''}
                              onChange={function(e) { setEditField(item.id, 'bible', e.target.value) }}
                              style={s.input} />
                          </div>
                        )}
                        <div style={s.field}>
                          <label style={s.label}>Ключові слова (через кому)</label>
                          <input value={form.keywords || ''}
                            onChange={function(e) { setEditField(item.id, 'keywords', e.target.value) }}
                            style={s.input} />
                        </div>
                      </div>
                      <div style={s.btnRow}>
                        <button onClick={function() { handleArchiveSave(item.id) }}
                          disabled={saving === item.id} style={s.btnPrimary}>
                          {saving === item.id ? 'Зберігаємо…' : '✓ Зберегти'}
                        </button>
                        <button onClick={function() { setExpandedArchive(null) }} style={s.btnGhost}>
                          Скасувати
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={s.pagination}>
              <button disabled={safePage === 1} onClick={function() { setArchivePage(safePage - 1) }}
                style={{ ...s.pageBtn, opacity: safePage === 1 ? 0.4 : 1 }}>←</button>
              <span style={{ fontSize: '13px', color: '#7a5030' }}>{safePage} / {totalPages}</span>
              <button disabled={safePage === totalPages} onClick={function() { setArchivePage(safePage + 1) }}
                style={{ ...s.pageBtn, opacity: safePage === totalPages ? 0.4 : 1 }}>→</button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  page: { maxWidth: '960px' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
  pageTitle: { margin: '0 0 6px', fontSize: '24px', fontWeight: '700', color: '#2d1f14' },
  pageDesc: { margin: 0, fontSize: '14px', color: '#9a7c63' },

  card: {
    background: '#fff', borderRadius: '14px', padding: '28px',
    boxShadow: '0 2px 16px rgba(92,61,46,0.07)', border: '1px solid #f0e4d4',
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '20px', paddingBottom: '14px', borderBottom: '1px solid #f5ede3',
  },
  cardIcon: { fontSize: '18px' },
  cardTitle: { fontSize: '15px', fontWeight: '700', color: '#2d1f14', flex: 1 },
  badge: {
    background: '#f0e6d8', color: '#8b6044',
    padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
  },
  empty: { color: '#b8a090', fontSize: '14px', padding: '12px 0', textAlign: 'center' },

  // pending
  pendingRow: { border: '1px solid #f0e4d4', borderRadius: '10px', overflow: 'hidden', background: '#fdfaf7', marginBottom: '4px' },
  pendingSummary: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', cursor: 'pointer' },
  thumb: { width: '80px', height: '45px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 },
  pendingInfo: { flex: 1, minWidth: 0 },
  pendingTitle: { fontSize: '14px', fontWeight: '600', color: '#2d1f14', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pendingMeta: { fontSize: '12px', color: '#9a7c63' },
  pendingActions: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  ytLink: { fontSize: '12px', color: '#c00', textDecoration: 'none', fontWeight: '600' },
  expandBtn: { fontSize: '18px', color: '#9a7c63', display: 'inline-block', transition: 'transform 0.15s' },

  // archive
  archiveRow: { border: '1px solid #f0e4d4', borderRadius: '8px', overflow: 'hidden', background: '#fdfaf7', marginBottom: '4px' },
  archiveSummary: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px' },
  archiveYear: { fontSize: '12px', fontWeight: '700', color: '#c4986a', width: '38px', flexShrink: 0 },
  archiveTitle: { flex: 1, fontSize: '13px', fontWeight: '600', color: '#2d1f14', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  archiveAuthor: { fontSize: '12px', color: '#9a7c63', width: '160px', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  archiveType: { fontSize: '11px', color: '#fff', background: '#c4986a', padding: '2px 8px', borderRadius: '10px', flexShrink: 0 },
  archiveActions: { display: 'flex', gap: '4px', flexShrink: 0 },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', padding: '2px 5px', borderRadius: '4px' },

  // form
  form: { padding: '16px 14px 14px', borderTop: '1px solid #f0e4d4', background: '#fff' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' },
  field: { marginBottom: '14px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#8b6044', marginBottom: '6px' },
  input: { width: '100%', padding: '9px 12px', border: '1.5px solid #e8d8c8', borderRadius: '7px', fontSize: '13px', color: '#2d1f14', background: '#fdfaf7', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' },

  btnRow: { display: 'flex', gap: '10px', marginTop: '4px' },
  btnPrimary: { padding: '9px 20px', background: 'linear-gradient(135deg, #6b4a32, #8b6044)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  btnGhost: { padding: '9px 16px', background: 'transparent', color: '#8b6044', border: '1.5px solid #e8d8c8', borderRadius: '7px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' },

  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px' },
  pageBtn: { padding: '6px 14px', border: '1.5px solid #e8d8c8', background: '#fff', color: '#6b4a32', borderRadius: '7px', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' },
}

export default AdminArchive
